// ConveyorTile is a map tile that shows a conveyor belt with an
// arrow on it.

import ConveyorTileActionResult, {
	ConveyorTileActionResultType,
} from 'app/src/engine/action-results/ConveyorTileActionResult';
import TileActionResult from 'app/src/engine/action-results/TileActionResult';
import { ConveyorTileArrow } from 'app/src/engine/tiles/ConveyorTile';
import scheduleAnimation from 'app/src/utils/scheduleAnimation';
import useValueChanged from 'app/src/utils/useValueChanged';
import * as React from 'react';

enum BeltGlareColor {
	Light,
	Dark,
}

enum BeltGlareOrder {
	Primary,
	Secondary,
}

function getClassNameForBeltContainer(
	arrow: ConveyorTileArrow,
	isOnScreenBeltContainer: boolean,
): string {
	switch (arrow) {
		case ConveyorTileArrow.Up:
		case ConveyorTileArrow.UpAndFromLeft:
		case ConveyorTileArrow.UpAndFromRight:
		case ConveyorTileArrow.MergeUp:
			return isOnScreenBeltContainer
				? 'ConveyorTile-beltContainer--onscreenToUp'
				: 'ConveyorTile-beltContainer--offscreenFromDown';

		case ConveyorTileArrow.Down:
		case ConveyorTileArrow.DownAndFromRight:
		case ConveyorTileArrow.DownAndFromLeft:
		case ConveyorTileArrow.MergeDown:
			return isOnScreenBeltContainer
				? 'ConveyorTile-beltContainer--onscreenToDown'
				: 'ConveyorTile-beltContainer--offscreenFromUp';

		case ConveyorTileArrow.Right:
		case ConveyorTileArrow.RightAndFromTop:
		case ConveyorTileArrow.RightAndFromBottom:
		case ConveyorTileArrow.MergeRight:
			return isOnScreenBeltContainer
				? 'ConveyorTile-beltContainer--onscreenToRight'
				: 'ConveyorTile-beltContainer--offscreenFromLeft';

		case ConveyorTileArrow.Left:
		case ConveyorTileArrow.LeftAndFromBottom:
		case ConveyorTileArrow.LeftAndFromTop:
		case ConveyorTileArrow.MergeLeft:
			return isOnScreenBeltContainer
				? 'ConveyorTile-beltContainer--onscreenToLeft'
				: 'ConveyorTile-beltContainer--offscreenFromRight';

		case ConveyorTileArrow.UpFromLeft:
			return isOnScreenBeltContainer
				? 'ConveyorTile-beltContainer--rotateTopLeft ConveyorTile-beltContainer--rotateCounterClockwiseToOff'
				: 'ConveyorTile-beltContainer--rotateTopLeft ConveyorTile-beltContainer--rotateCounterClockwiseToOn';

		case ConveyorTileArrow.UpFromRight:
			return isOnScreenBeltContainer
				? 'ConveyorTile-beltContainer--rotateTopRight ConveyorTile-beltContainer--rotateClockwiseToOff'
				: 'ConveyorTile-beltContainer--rotateTopRight ConveyorTile-beltContainer--rotateClockwiseToOn';

		case ConveyorTileArrow.LeftFromTop:
			return isOnScreenBeltContainer
				? 'ConveyorTile-beltContainer--rotateTopLeft ConveyorTile-beltContainer--rotateClockwiseToOff'
				: 'ConveyorTile-beltContainer--rotateTopLeft ConveyorTile-beltContainer--rotateClockwiseToOn';

		case ConveyorTileArrow.LeftFromBottom:
			return isOnScreenBeltContainer
				? 'ConveyorTile-beltContainer--rotateBottomLeft ConveyorTile-beltContainer--rotateCounterClockwiseToOff'
				: 'ConveyorTile-beltContainer--rotateBottomLeft ConveyorTile-beltContainer--rotateCounterClockwiseToOn';

		case ConveyorTileArrow.RightFromTop:
			return isOnScreenBeltContainer
				? 'ConveyorTile-beltContainer--rotateTopRight ConveyorTile-beltContainer--rotateCounterClockwiseToOff'
				: 'ConveyorTile-beltContainer--rotateTopRight ConveyorTile-beltContainer--rotateCounterClockwiseToOn';

		case ConveyorTileArrow.RightFromBottom:
			return isOnScreenBeltContainer
				? 'ConveyorTile-beltContainer--rotateBottomRight ConveyorTile-beltContainer--rotateClockwiseToOff'
				: 'ConveyorTile-beltContainer--rotateBottomRight ConveyorTile-beltContainer--rotateClockwiseToOn';

		case ConveyorTileArrow.DownFromRight:
			return isOnScreenBeltContainer
				? 'ConveyorTile-beltContainer--rotateBottomRight ConveyorTile-beltContainer--rotateCounterClockwiseToOff'
				: 'ConveyorTile-beltContainer--rotateBottomRight ConveyorTile-beltContainer--rotateCounterClockwiseToOn';

		case ConveyorTileArrow.DownFromLeft:
			return isOnScreenBeltContainer
				? 'ConveyorTile-beltContainer--rotateBottomLeft ConveyorTile-beltContainer--rotateClockwiseToOff'
				: 'ConveyorTile-beltContainer--rotateBottomLeft ConveyorTile-beltContainer--rotateClockwiseToOn';
	}
}

function getClassNameForArrow(arrow: ConveyorTileArrow): string {
	switch (arrow) {
		case ConveyorTileArrow.Left:
		case ConveyorTileArrow.Right:
		case ConveyorTileArrow.Up:
		case ConveyorTileArrow.Down:
			return 'ConveyorTile-arrow--up';

		case ConveyorTileArrow.LeftFromBottom:
		case ConveyorTileArrow.UpFromLeft:
		case ConveyorTileArrow.RightFromTop:
		case ConveyorTileArrow.DownFromRight:
			return 'ConveyorTile-arrow--turnLeft';

		case ConveyorTileArrow.LeftFromTop:
		case ConveyorTileArrow.UpFromRight:
		case ConveyorTileArrow.RightFromBottom:
		case ConveyorTileArrow.DownFromLeft:
			return 'ConveyorTile-arrow--turnRight';

		case ConveyorTileArrow.LeftAndFromBottom:
		case ConveyorTileArrow.UpAndFromLeft:
		case ConveyorTileArrow.RightAndFromTop:
		case ConveyorTileArrow.DownAndFromRight:
			return 'ConveyorTile-arrow--upAndFromLeft';

		case ConveyorTileArrow.LeftAndFromTop:
		case ConveyorTileArrow.UpAndFromRight:
		case ConveyorTileArrow.RightAndFromBottom:
		case ConveyorTileArrow.DownAndFromLeft:
			return 'ConveyorTile-arrow--upAndFromRight';

		case ConveyorTileArrow.MergeLeft:
		case ConveyorTileArrow.MergeUp:
		case ConveyorTileArrow.MergeRight:
		case ConveyorTileArrow.MergeDown:
			return 'ConveyorTile-arrow--mergeUp';
	}
}

function getRotationClassNameForArrow(arrow: ConveyorTileArrow): string {
	switch (arrow) {
		case ConveyorTileArrow.Up:
		case ConveyorTileArrow.UpAndFromLeft:
		case ConveyorTileArrow.UpAndFromRight:
		case ConveyorTileArrow.MergeUp:
			return 'ConveyorTile-arrow--orientUp';

		case ConveyorTileArrow.Right:
		case ConveyorTileArrow.RightAndFromTop:
		case ConveyorTileArrow.RightAndFromBottom:
		case ConveyorTileArrow.MergeRight:
			return 'ConveyorTile-arrow--orientRight';

		case ConveyorTileArrow.Down:
		case ConveyorTileArrow.DownAndFromRight:
		case ConveyorTileArrow.DownAndFromLeft:
		case ConveyorTileArrow.MergeDown:
			return 'ConveyorTile-arrow--orientDown';

		case ConveyorTileArrow.Left:
		case ConveyorTileArrow.LeftAndFromBottom:
		case ConveyorTileArrow.LeftAndFromTop:
		case ConveyorTileArrow.MergeLeft:
			return 'ConveyorTile-arrow--orientLeft';

		case ConveyorTileArrow.UpFromLeft:
			return 'ConveyorTile-arrow--orientRightLeftTurn';
		case ConveyorTileArrow.DownFromLeft:
			return 'ConveyorTile-arrow--orientRightRightTurn';

		case ConveyorTileArrow.LeftFromTop:
			return 'ConveyorTile-arrow--orientDownRightTurn';
		case ConveyorTileArrow.RightFromTop:
			return 'ConveyorTile-arrow--orientDownLeftTurn';

		case ConveyorTileArrow.UpFromRight:
			return 'ConveyorTile-arrow--orientLeftRightTurn';
		case ConveyorTileArrow.DownFromRight:
			return 'ConveyorTile-arrow--orientLeftLeftTurn';

		case ConveyorTileArrow.RightFromBottom:
			return 'ConveyorTile-arrow--orientUpRightTurn';
		case ConveyorTileArrow.LeftFromBottom:
			return 'ConveyorTile-arrow--orientUpLeftTurn';
	}
}

function getRotationClassNameForBelt(arrow: ConveyorTileArrow): string {
	switch (arrow) {
		case ConveyorTileArrow.Up:
		case ConveyorTileArrow.UpAndFromLeft:
		case ConveyorTileArrow.UpAndFromRight:
		case ConveyorTileArrow.MergeUp:
		case ConveyorTileArrow.Down:
		case ConveyorTileArrow.DownAndFromRight:
		case ConveyorTileArrow.DownAndFromLeft:
		case ConveyorTileArrow.MergeDown:
			return 'ConveyorTile-belt--orientUp';

		case ConveyorTileArrow.Right:
		case ConveyorTileArrow.RightAndFromTop:
		case ConveyorTileArrow.RightAndFromBottom:
		case ConveyorTileArrow.MergeRight:
		case ConveyorTileArrow.Left:
		case ConveyorTileArrow.LeftAndFromBottom:
		case ConveyorTileArrow.LeftAndFromTop:
		case ConveyorTileArrow.MergeLeft:
			return 'ConveyorTile-belt--orientRight';

		// Corner tiles don't need belts. Only the arrow animates.
		case ConveyorTileArrow.UpFromLeft:
		case ConveyorTileArrow.LeftFromTop:
		case ConveyorTileArrow.RightFromTop:
		case ConveyorTileArrow.UpFromRight:
		case ConveyorTileArrow.DownFromRight:
		case ConveyorTileArrow.RightFromBottom:
		case ConveyorTileArrow.DownFromLeft:
		case ConveyorTileArrow.LeftFromBottom:
			return 'ConveyorTile-belt--hidden';
	}
}

function getClassNameForBeltGlare(
	arrow: ConveyorTileArrow,
	color: BeltGlareColor,
	order: BeltGlareOrder,
): string | null {
	switch (arrow) {
		case ConveyorTileArrow.Up:
		case ConveyorTileArrow.UpAndFromLeft:
		case ConveyorTileArrow.UpAndFromRight:
		case ConveyorTileArrow.MergeUp:
		case ConveyorTileArrow.Down:
		case ConveyorTileArrow.DownAndFromRight:
		case ConveyorTileArrow.DownAndFromLeft:
		case ConveyorTileArrow.MergeDown:
			if (order === BeltGlareOrder.Secondary) {
				return null;
			}

			if (color === BeltGlareColor.Light) {
				return 'ConveyorTile-beltGlareLight--top';
			} else {
				return 'ConveyorTile-beltGlareDark--bottom';
			}

		case ConveyorTileArrow.Right:
		case ConveyorTileArrow.RightAndFromTop:
		case ConveyorTileArrow.RightAndFromBottom:
		case ConveyorTileArrow.MergeRight:
		case ConveyorTileArrow.Left:
		case ConveyorTileArrow.LeftAndFromBottom:
		case ConveyorTileArrow.LeftAndFromTop:
		case ConveyorTileArrow.MergeLeft:
			if (order === BeltGlareOrder.Secondary) {
				return null;
			}

			if (color === BeltGlareColor.Light) {
				return 'ConveyorTile-beltGlareLight--right';
			} else {
				return 'ConveyorTile-beltGlareDark--left';
			}

		case ConveyorTileArrow.UpFromLeft:
		case ConveyorTileArrow.LeftFromTop:
			if (order === BeltGlareOrder.Secondary) {
				return null;
			}

			if (color === BeltGlareColor.Light) {
				return 'ConveyorTile-beltGlareLight--top';
			} else {
				return 'ConveyorTile-beltGlareDark--left';
			}

		case ConveyorTileArrow.RightFromTop:
		case ConveyorTileArrow.UpFromRight:
			if (color === BeltGlareColor.Dark) {
				return null;
			}

			if (order === BeltGlareOrder.Primary) {
				return 'ConveyorTile-beltGlareLight--top';
			} else {
				return 'ConveyorTile-beltGlareLight--right';
			}

		case ConveyorTileArrow.DownFromRight:
		case ConveyorTileArrow.RightFromBottom:
			if (order === BeltGlareOrder.Secondary) {
				return null;
			}

			if (color === BeltGlareColor.Light) {
				return 'ConveyorTile-beltGlareLight--right';
			} else {
				return 'ConveyorTile-beltGlareDark--bottom';
			}

		case ConveyorTileArrow.DownFromLeft:
		case ConveyorTileArrow.LeftFromBottom:
			if (color === BeltGlareColor.Light) {
				return null;
			}

			if (order === BeltGlareOrder.Primary) {
				return 'ConveyorTile-beltGlareDark--left';
			} else {
				return 'ConveyorTile-beltGlareDark--bottom';
			}
	}
}

function getRotationClassNameForFloor(arrow: ConveyorTileArrow): string {
	switch (arrow) {
		case ConveyorTileArrow.Up:
		case ConveyorTileArrow.UpAndFromLeft:
		case ConveyorTileArrow.UpAndFromRight:
		case ConveyorTileArrow.MergeUp:
		case ConveyorTileArrow.Down:
		case ConveyorTileArrow.DownAndFromRight:
		case ConveyorTileArrow.DownAndFromLeft:
		case ConveyorTileArrow.MergeDown:
			return 'ConveyorTile-floor--orientUp';

		case ConveyorTileArrow.Right:
		case ConveyorTileArrow.RightAndFromTop:
		case ConveyorTileArrow.RightAndFromBottom:
		case ConveyorTileArrow.MergeRight:
		case ConveyorTileArrow.Left:
		case ConveyorTileArrow.LeftAndFromBottom:
		case ConveyorTileArrow.LeftAndFromTop:
		case ConveyorTileArrow.MergeLeft:
			return 'ConveyorTile-floor--orientRight';

		case ConveyorTileArrow.UpFromLeft:
		case ConveyorTileArrow.LeftFromTop:
			return 'ConveyorTile-floor--turn90';

		case ConveyorTileArrow.RightFromTop:
		case ConveyorTileArrow.UpFromRight:
			return 'ConveyorTile-floor--turn180';

		case ConveyorTileArrow.DownFromRight:
		case ConveyorTileArrow.RightFromBottom:
			return 'ConveyorTile-floor--turn270';

		case ConveyorTileArrow.DownFromLeft:
		case ConveyorTileArrow.LeftFromBottom:
			return 'ConveyorTile-floor--turn0';
	}
}

function getBaseClassNameForFloor(arrow: ConveyorTileArrow): string {
	switch (arrow) {
		case ConveyorTileArrow.LeftFromBottom:
		case ConveyorTileArrow.UpFromLeft:
		case ConveyorTileArrow.RightFromTop:
		case ConveyorTileArrow.DownFromRight:
		case ConveyorTileArrow.LeftFromTop:
		case ConveyorTileArrow.UpFromRight:
		case ConveyorTileArrow.RightFromBottom:
		case ConveyorTileArrow.DownFromLeft:
			return 'ConveyorTile-floor--turn';

		default:
			return 'ConveyorTile-floor--straight';
	}
}

type Props = {
	arrow: ConveyorTileArrow;
	row: number;
	col: number;
	actionResults?: TileActionResult[] | null;
	completeAnimation?: () => void;
};

export default function ConveyorTile({
	arrow,
	actionResults,
	completeAnimation,
}: Props): React.ReactElement | null {
	const onscreenBeltContainerRef = React.useRef<HTMLDivElement | null>(null);
	const offscreenBeltContainerRef = React.useRef<HTMLDivElement | null>(null);

	// When we're running animations, we need to be able to cancel.
	const cancelAnimationSequenceRef = React.useRef<(() => void) | null>(null);

	// If this component is unmounted (this happens when the user resets), kill
	// any ongoing animations.
	React.useEffect(
		() => (): void => {
			if (cancelAnimationSequenceRef.current) {
				cancelAnimationSequenceRef.current();
			}
		},
		[],
	);

	// Handle animation changes.
	const actionResult =
		actionResults &&
		(actionResults.find((result) => result instanceof ConveyorTileActionResult) as
			| ConveyorTileActionResult
			| undefined);
	const actionResultChanged = useValueChanged(actionResult);

	React.useEffect(() => {
		if (!actionResult || !actionResultChanged || !completeAnimation) {
			return;
		}

		// If the action result is ours, animate the belts.
		if (
			actionResult.type === ConveyorTileActionResultType.Move &&
			onscreenBeltContainerRef.current &&
			offscreenBeltContainerRef.current
		) {
			onscreenBeltContainerRef.current.classList.add('ConveyorTile-beltContainer--animatingMove');
			offscreenBeltContainerRef.current.classList.add('ConveyorTile-beltContainer--animatingMove');
			onscreenBeltContainerRef.current.classList.add('ConveyorTile-beltContainer--moving');
			offscreenBeltContainerRef.current.classList.add('ConveyorTile-beltContainer--moving');

			cancelAnimationSequenceRef.current = scheduleAnimation([
				[
					(): void => {
						if (onscreenBeltContainerRef.current && offscreenBeltContainerRef.current) {
							onscreenBeltContainerRef.current.classList.remove(
								'ConveyorTile-beltContainer--animatingMove',
							);
							offscreenBeltContainerRef.current.classList.remove(
								'ConveyorTile-beltContainer--animatingMove',
							);
							onscreenBeltContainerRef.current.classList.remove(
								'ConveyorTile-beltContainer--moving',
							);
							offscreenBeltContainerRef.current.classList.remove(
								'ConveyorTile-beltContainer--moving',
							);
						}

						completeAnimation();
						cancelAnimationSequenceRef.current = null;
					},
					600,
				],
			]);
		} else if (
			actionResult.type === ConveyorTileActionResultType.Bounce &&
			onscreenBeltContainerRef.current &&
			offscreenBeltContainerRef.current
		) {
			onscreenBeltContainerRef.current.classList.add('ConveyorTile-beltContainer--animatingBounce');
			offscreenBeltContainerRef.current.classList.add(
				'ConveyorTile-beltContainer--animatingBounce',
			);
			onscreenBeltContainerRef.current.classList.add('ConveyorTile-beltContainer--bouncing');
			offscreenBeltContainerRef.current.classList.add('ConveyorTile-beltContainer--bouncing');

			cancelAnimationSequenceRef.current = scheduleAnimation([
				[
					(): void => {
						if (onscreenBeltContainerRef.current && offscreenBeltContainerRef.current) {
							onscreenBeltContainerRef.current.classList.remove(
								'ConveyorTile-beltContainer--bouncing',
							);
							offscreenBeltContainerRef.current.classList.remove(
								'ConveyorTile-beltContainer--bouncing',
							);
						}
					},
					300,
				],
				[
					(): void => {
						if (onscreenBeltContainerRef.current && offscreenBeltContainerRef.current) {
							onscreenBeltContainerRef.current.classList.remove(
								'ConveyorTile-beltContainer--animatingBounce',
							);
							offscreenBeltContainerRef.current.classList.remove(
								'ConveyorTile-beltContainer--animatingBounce',
							);
						}

						completeAnimation();
						cancelAnimationSequenceRef.current = null;
					},
					300,
				],
			]);
		}
	});

	const floorClassNames = [
		'ConveyorTile-floor',
		getBaseClassNameForFloor(arrow),
		getRotationClassNameForFloor(arrow),
	];
	const arrowClassNames = [
		'ConveyorTile-arrow',
		getClassNameForArrow(arrow),
		getRotationClassNameForArrow(arrow),
	];
	const beltContainerOnscreenClassNames = [
		'ConveyorTile-beltContainer',
		getClassNameForBeltContainer(arrow, true),
	];
	const beltContainerOffscreenClassNames = [
		'ConveyorTile-beltContainer',
		getClassNameForBeltContainer(arrow, false),
	];
	const beltClassNames = ['ConveyorTile-belt', getRotationClassNameForBelt(arrow)];

	const primaryBeltGlareLightClassName = getClassNameForBeltGlare(
		arrow,
		BeltGlareColor.Light,
		BeltGlareOrder.Primary,
	);
	const secondaryBeltGlareLightClassName = getClassNameForBeltGlare(
		arrow,
		BeltGlareColor.Light,
		BeltGlareOrder.Secondary,
	);
	const primaryBeltGlareDarkClassName = getClassNameForBeltGlare(
		arrow,
		BeltGlareColor.Dark,
		BeltGlareOrder.Primary,
	);
	const secondaryBeltGlareDarkClassName = getClassNameForBeltGlare(
		arrow,
		BeltGlareColor.Dark,
		BeltGlareOrder.Secondary,
	);

	return (
		<div className="ConveyorTile">
			<div className={floorClassNames.join(' ')}></div>
			<div ref={onscreenBeltContainerRef} className={beltContainerOnscreenClassNames.join(' ')}>
				<div className={arrowClassNames.join(' ')}></div>
				<div className={beltClassNames.join(' ')}></div>
			</div>
			<div ref={offscreenBeltContainerRef} className={beltContainerOffscreenClassNames.join(' ')}>
				<div className={arrowClassNames.join(' ')}></div>
				<div className={beltClassNames.join(' ')}></div>
			</div>
			{primaryBeltGlareLightClassName && (
				<div className={`ConveyorTile-beltGlareLight ${primaryBeltGlareLightClassName}`}></div>
			)}
			{secondaryBeltGlareLightClassName && (
				<div className={`ConveyorTile-beltGlareLight ${secondaryBeltGlareLightClassName}`}></div>
			)}
			{primaryBeltGlareDarkClassName && (
				<div className={`ConveyorTile-beltGlareDark ${primaryBeltGlareDarkClassName}`}></div>
			)}
			{secondaryBeltGlareDarkClassName && (
				<div className={`ConveyorTile-beltGlareDark ${secondaryBeltGlareDarkClassName}`}></div>
			)}
		</div>
	);
}
